import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { ArrowLeft, Target, TrendingUp, Users, Globe, Heart, CheckCircle } from 'lucide-react';

const Impact = () => {
  const impactAreas = [
    {
      icon: <Users className="h-8 w-8" />,
      title: "User Empowerment",
      description: "We create tools that enhance people's capabilities and help them achieve their goals more effectively."
    },
    {
      icon: <TrendingUp className="h-8 w-8" />,
      title: "Business Growth",
      description: "Our solutions help businesses streamline operations, increase productivity, and achieve sustainable growth."
    },
    {
      icon: <Globe className="h-8 w-8" />,
      title: "Community Development",
      description: "Through our humanitarian fund, we support community initiatives that create lasting positive change."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Social Responsibility",
      description: "We integrate social impact considerations into all our business decisions and product development."
    }
  ];

  const impactMetrics = [
    {
      number: "10,000+",
      label: "Users Empowered",
      description: "People using our tools to improve their productivity and work-life balance"
    },
    {
      number: "500+",
      label: "Businesses Supported",
      description: "Companies that have streamlined their operations with our solutions"
    },
    {
      number: "2.5%",
      label: "Revenue for Good",
      description: "Percentage of all revenue dedicated to humanitarian initiatives"
    },
    {
      number: "50+",
      label: "Communities Helped",
      description: "Local and international communities supported through our giving program"
    }
  ];

  const impactStories = [
    {
      title: "Small Business Transformation",
      description: "A local Perth consulting firm increased their client capacity by 40% using GoFella's project management features, allowing them to serve more businesses while maintaining quality.",
      outcome: "40% increase in capacity"
    },
    {
      title: "Remote Team Coordination",
      description: "A distributed team across three countries improved their collaboration efficiency by 60% through our integrated communication and task management platform.",
      outcome: "60% better collaboration"
    },
    {
      title: "Community Food Program", 
      description: "Our humanitarian fund supported a food security program in remote Australian communities, providing regular meals for 200 families over six months.",
      outcome: "200 families supported"
    },
    {
      title: "Educational Technology Access",
      description: "Partnership with educational nonprofits to provide technology training and tools to underserved student populations in developing regions.",
      outcome: "1,000+ students reached"
    }
  ];

  const futureGoals = [
    "Reach 100,000 users by 2026 with productivity-enhancing tools",
    "Support 1,000 businesses in their digital transformation journey",
    "Contribute $1 million annually to humanitarian initiatives by 2027",
    "Establish partnerships with 100 nonprofit organizations globally",
    "Create 50 new jobs in Perth's technology sector",
    "Achieve carbon-neutral operations across all business activities"
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/">
          <Button variant="outline" className="mb-8 text-vincotek-blue border-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group">
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </Button>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="pb-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-vincotek-yellow to-vincotek-blue rounded-full flex items-center justify-center mx-auto mb-8">
              <Target className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight mb-6">
              Impact
            </h1>
            <p className="text-xl text-vincotek-blue font-semibold mb-6">
              Turning Ideas into Solutions That Make a Difference
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mb-12">
              At Vincotek, impact is how we measure success. We're not satisfied with just building good technology—
              we're committed to creating solutions that generate measurable positive outcomes for users, businesses, 
              and communities around the world.
            </p>
            
            <div className="bg-gradient-to-br from-gray-50 to-blue-50/30 rounded-2xl p-8 border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Impact is not about what we build, but about the positive change we create in the world."
              </blockquote>
              <p className="text-gray-600">
                — Vincotek's measure of true success
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Areas of Impact
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our impact spans multiple dimensions, from individual productivity to global community support.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {impactAreas.map((area, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 bg-white border-0">
                <CardContent className="p-8">
                  <div className="text-vincotek-blue mb-6 group-hover:scale-110 transition-transform duration-300">
                    {area.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {area.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {area.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Metrics */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Our Impact by Numbers
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Concrete metrics that demonstrate the real-world impact of our work and commitments.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactMetrics.map((metric, index) => (
              <Card key={index} className="text-center bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-vincotek-blue mb-2">
                    {metric.number}
                  </div>
                  <div className="text-lg font-semibold text-vincotek-dark mb-2">
                    {metric.label}
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {metric.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Impact Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real examples of how our solutions create positive change in people's lives and communities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {impactStories.map((story, index) => (
              <Card key={index} className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-vincotek-dark">
                      {story.title}
                    </h3>
                    <span className="text-sm font-bold text-vincotek-blue bg-vincotek-blue/10 px-3 py-1 rounded-full">
                      {story.outcome}
                    </span>
                  </div>
                  <p className="text-gray-600 leading-relaxed">
                    {story.description}
                  </p>
                  <div className="mt-4 flex items-center text-vincotek-blue">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Verified Impact</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Future Impact Goals */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Future Impact Goals
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Our roadmap for creating even greater positive impact in the coming years.
              </p>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="flex items-center mb-6">
                <Target className="h-8 w-8 text-vincotek-yellow mr-4" />
                <h3 className="text-2xl font-semibold">2025-2027 Impact Objectives</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {futureGoals.map((goal, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-3 h-3 bg-vincotek-yellow rounded-full mt-2 mr-4 flex-shrink-0"></div>
                    <span className="text-gray-300 leading-relaxed">{goal}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
              Join Our Impact Journey
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Whether you're a business looking to improve efficiency, a nonprofit seeking technology support, 
              or an individual wanting to increase productivity, you can be part of creating positive impact with Vincotek.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/products-services">
                <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                  Explore Our Solutions
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                  Start Making Impact
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Impact;